const UrlEncodeInfo = () => {
  return (
    <>
      <span className="mb-4 d-block">
        URL encoding, also known as percent encoding, is a mechanism for
        encoding information in a Uniform Resource Locator (URL) in a way that
        is safe for transmission over the internet. This process is essential
        for ensuring that URLs are correctly interpreted by web servers and
        browsers, as URLs can only be sent over the Internet using the ASCII
        character set. Understanding how URL encoding works involves exploring
        its purpose, the encoding process, and its applications.
      </span>
      <strong className="d-block mb-4">Purpose and Rationale</strong>
      <span className="d-block mb-4">
        URLs often contain characters that have special meanings or are not
        allowed in certain parts of a URL. For example, spaces, punctuation
        marks, and non-ASCII characters can cause issues if not properly
        encoded. URL encoding converts these characters into a format that can
        be safely transmitted over the internet. This is crucial for maintaining
        the integrity of the data being sent and ensuring that web servers and
        browsers can correctly interpret the URL.
      </span>
      <strong className="d-block mb-4">Encoding Process</strong>
      <ol>
        <li>
          Identifying Characters to Encode:
          <span className="mt-3 d-block mb-2">
            URL encoding targets characters that are not allowed in a URL or
            have special meanings. These include:
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">
              Reserved characters:
            </strong>
            Characters like `:`, `/`, `?`, `#`, `[`, `]`, `@`, `!`, `$`, `&`,
            `'`, `(`, `)`, `*`, `+`, `,`, `;`, and `=` have special meanings in
            URLs.
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">Unsafe characters:</strong>
            Characters like spaces, `&lt;`, `&gt;`, `#`, `%`, `{`, `}`, `|`,
            `\`, `^`, `~`, `[`, `]`, and `` ` `` are considered unsafe.
          </span>
          <span className="d-block mt-2 mb-3">
            <strong className="me-2 d-inline-block">
              Non-ASCII characters:
            </strong>
            Characters outside the ASCII range (0-127) need to be encoded.
          </span>
        </li>
        <li>
          Encoding Characters:
          <span className="mt-3 d-block mb-2">
            The encoding process involves converting each character that needs
            to be encoded into a percent sign `%` followed by two hexadecimal
            digits representing the character's ASCII value. For example:
          </span>
          <span className="d-block mt-2">
            A space (` `) is encoded as `%20`.
          </span>
          <span className="d-block mt-2">
            A plus sign (`+`) is encoded as `%2B`.
          </span>
          <span className="d-block mt-2 mb-3">
            A non-ASCII character, such as `é`, is first converted to its UTF-8
            byte sequence and then each byte is percent-encoded.
          </span>
        </li>
        <li>
          Handling Special Cases:
          <span className="mt-3 d-block mb-4">
            Spaces can also be encoded as a plus sign (`+`) in the query string
            part of a URL, although `%20` is more universally accepted.
          </span>
          <span className="mt-3 d-block mb-4">
            Reserved characters are only encoded when they are not used for
            their intended purpose. For example, a slash (`/`) used to separate
            path segments is not encoded.
          </span>
        </li>
      </ol>
      <strong className="d-block mb-4">Applications of URL Encoding</strong>
      <span className="d-block mb-2">
        URL encoding is used in various scenarios where data needs to be
        transmitted via URLs:
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Form Submission:</strong>
        When a web form is submitted, the data is encoded into a query string
        that is appended to the URL. This ensures that all characters are safely
        transmitted and correctly interpreted by the server.
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Query Parameters:</strong>
        URL encoding is used to encode query parameters in a URL, ensuring that
        special characters do not interfere with the URL structure.
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Data Transmission:</strong>
        URL encoding is essential for transmitting data that includes special
        characters, such as JSON or XML data, within a URL.
      </span>
      <strong className="d-block mb-4 mt-4">
        Limitations and Considerations
      </strong>
      <span className="d-block mb-2">
        While URL encoding is effective for ensuring safe data transmission, it
        has limitations:
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Increased Length:</strong>
        Encoding increases the length of the URL, which can be a concern for
        systems with URL length restrictions.
      </span>
      <span className="d-block mt-2 mb-4">
        <strong className="me-2 d-inline-block">Complexity:</strong>
        Manually encoding and decoding URLs can be complex, especially for
        non-ASCII characters. However, most programming languages and web
        frameworks provide built-in functions to handle URL encoding and
        decoding.
      </span>
      <span className="d-block">
        In conclusion, URL encoding is a vital process for ensuring that URLs
        are correctly interpreted and transmitted over the internet. By
        converting special and non-ASCII characters into a safe format, URL
        encoding maintains the integrity and functionality of web
        communications.
      </span>
    </>
  );
};

export default UrlEncodeInfo;
